'use client'

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { orpc } from '@/lib/orpcClient'
import type { ModuleKey, ModuleListItem, FeatureListItem } from '@cloud/modules'

/**
 * Result type from the modules.list API call
 */
export type ModulesListItem = ModuleListItem

interface UseModulesResult {
  modules: ModulesListItem[]
  isLoading: boolean
  isError: boolean
  error: Error | null
  /**
   * Check if a module is enabled for the current tenant
   * @param key - The module key to check (hr, finance, inventory, crm, projects)
   * @returns true if the module status is 'active'
   */
  isModuleEnabled: (key: ModuleKey | string) => boolean
  /**
   * Check if a feature is enabled for a specific module
   * @param moduleKey - The module key
   * @param featureKey - The feature key to check
   * @returns true if the feature is enabled
   */
  isFeatureEnabled: (moduleKey: ModuleKey | string, featureKey: string) => boolean
  /**
   * Get a specific module by key
   * @param key - The module key
   * @returns The module data or undefined if not found
   */
  getModule: (key: ModuleKey | string) => ModulesListItem | undefined
  /**
   * Get features for a specific module
   * @param moduleKey - The module key
   * @returns Array of features with their enabled status
   */
  getModuleFeatures: (moduleKey: ModuleKey | string) => FeatureListItem[]
  /**
   * Refetch modules data
   */
  refetch: () => void
}

/**
 * React hook to access and manage module state
 * 
 * Calls the modules.list API and provides helper functions to check
 * module and feature enabled status.
 * 
 * Uses the oRPC client's built-in caching with staleTime to prevent
 * redundant API calls.
 */
export function useModules(): UseModulesResult {
  // Use the oRPC client with TanStack Query integration
  // The orpc.utils are already configured with proper caching
  const { data, isLoading, isError, error, refetch } = useQuery(orpc.modules.list.queryOptions())

  const modules = useMemo(() => data ?? [], [data])

  const isModuleEnabled = useMemo(() => {
    return (key: ModuleKey | string): boolean => {
      const module = modules.find((m) => m.key === key)
      return module?.status === 'active'
    }
  }, [modules])

  const isFeatureEnabled = useMemo(() => {
    return (moduleKey: ModuleKey | string, featureKey: string): boolean => {
      const module = modules.find((m) => m.key === moduleKey)
      if (!module) return false
      
      const feature = module.features.find((f) => f.featureKey === featureKey)
      return feature?.enabled ?? false
    }
  }, [modules])

  const getModule = useMemo(() => {
    return (key: ModuleKey | string): ModulesListItem | undefined => {
      return modules.find((m) => m.key === key)
    }
  }, [modules])

  const getModuleFeatures = useMemo(() => {
    return (moduleKey: ModuleKey | string): FeatureListItem[] => {
      const module = modules.find((m) => m.key === moduleKey)
      if (!module) return []
      
      return module.features.map((f) => ({
        key: f.featureKey,
        name: f.featureKey, // Could be enhanced with feature name from manifest
        description: '',
        enabled: f.enabled,
        config: f.config,
      }))
    }
  }, [modules])

  return {
    modules,
    isLoading,
    isError,
    error: error as Error | null,
    isModuleEnabled,
    isFeatureEnabled,
    getModule,
    getModuleFeatures,
    refetch,
  }
}
